import { StoredItem, eqItemSlots, parseItemStat } from "LibMargoWorld/item";
import { MargoURLs } from "LibMargoWorld/micc";

import { getEqItemBySlot } from "./eq-set";

const currentTip = {
    owner: <HTMLElement>null,
    el: <HTMLDivElement>null,
    posEl: <HTMLDivElement>null,
};

function createBasicTip(txt: string, type = "t_npc") {
    const tip = document.createElement("div");
    tip.classList.add("tip-wrapper", "normal-tip", "custom");
    tip.dataset.type = type;
    tip.innerHTML = `<div class="content">${txt}</div>`;
    return tip;
}

function getItemRarity(item: StoredItem) {
    const st = parseItemStat(item.stat);
    return st.rarity ?? "common";
}

function createBasicTipFromElement(el: HTMLElement, type = "t_npc") {
    const tip = document.createElement("div");
    tip.classList.add("tip-wrapper", "normal-tip", "custom");
    tip.dataset.type = type;
    tip.appendChild(el);
    return tip;
}

const helper = document.createElement("div");
function getItemTip(item: StoredItem) {
    const baseHtml = window.MargoTipsParser.getTip(item);
    helper.innerHTML = `<div class="content">${baseHtml}</div>`;
    const head = helper.querySelector(".item-head");
    if (head instanceof HTMLElement) {
        const icon = document.createElement("span");
        icon.classList.add("item");
        const img = document.createElement("img");
        img.src = MargoURLs.itemPath + item.icon;
        img.classList.add("margonem_item", `rarity-${getItemRarity(item)}`);
        icon.appendChild(img);
        head.prepend(icon);
    }
    const value = helper.querySelector(".value-item");
    const level = helper.querySelector(".level-required");
    if (value && level) {
        const wrapper = document.createElement("div");
        wrapper.classList.add("level-and-value");
        level.parentElement.insertBefore(wrapper, level);
        wrapper.appendChild(level);
        wrapper.appendChild(value);
    }
    return <HTMLElement>helper.firstChild;
}

function clamp(n1: number, n2: number, n3: number) {
    return Math.max(n1, Math.min(n2, n3));
}

function getPagePositionFromEvent(e: MouseEvent | TouchEvent) {
    if (e instanceof MouseEvent) {
        return { x: e.pageX, y: e.pageY };
    } else {
        let x = 0,
            y = 0,
            count = e.touches.length;
        for (let i = 0; i < count; ++i) {
            const touch = e.touches.item(i);
            x += touch.pageX;
            y += touch.pageY;
        }
        x = x / count;
        y = y / count;
        return { x, y };
    }
}

function positionTip(tip: HTMLDivElement, e: MouseEvent | TouchEvent, positionEl?: HTMLDivElement) {
    const maxX = document.body.scrollWidth;
    const maxY = document.body.scrollHeight;

    const w = tip.offsetWidth;
    const h = tip.offsetHeight;
    const pos = getPagePositionFromEvent(e);
    const x = pos.x;
    const y = pos.y;

    let left = 0;
    let top = 0;
    let flipX = 1;
    let flipY = 1;
    if (x + 15 + w > maxX) {
        left = x - 15 - w;
        flipX = -1;
    } else {
        left = x + 15;
    }
    if (y + 15 + h > maxY) {
        top = y - 15 - h;
        flipY = -1;
    } else {
        top = y + 15;
    }

    if (positionEl && positionEl != tip) {
        left -= positionEl.offsetLeft * flipX;
        top -= positionEl.offsetTop * flipY;
    }

    tip.style.left = clamp(10, left, maxX - w - 10) + "px";
    tip.style.top = clamp(10, top, maxY - h - 10) + "px";
}

function removeExistingTips() {
    Array.from(document.querySelectorAll(".tip-wrapper:not(.static)")).forEach((node) => node.remove());
}

function createComparisonNote() {
    const el = document.createElement("div");
    el.classList.add("tip-comparison-note");
    el.innerText = "Przedmiot z zestawu ekwipunku.";
    return el;
}

export function appendComparisonNote($el: HTMLElement) {
    $el.querySelector(".content").prepend(createComparisonNote());
}

function addTip(e: MouseEvent | TouchEvent, tipEl: HTMLDivElement, owner: HTMLElement, posEl?: HTMLDivElement) {
    removeExistingTips();
    document.body.appendChild(tipEl);
    currentTip.owner = owner;
    currentTip.el = tipEl;
    currentTip.posEl = posEl;
    positionTip(tipEl, e, posEl);
}

function clearTip() {
    currentTip.el.remove();
    currentTip.owner = null;
    currentTip.el = null;
    currentTip.posEl = null;
}

function tipStart(e: MouseEvent | TouchEvent) {
    if (!(e.target instanceof HTMLElement)) {
        console.log("Not html element...?", e.target);
        return;
    }

    // Comparison tips get rather big - disable them on mobile
    const isComparisonAllowed = window.innerWidth >= 768;

    const tipEl = e.target.closest("[data-tip]");
    if (tipEl && tipEl instanceof HTMLElement) {
        const tip = createBasicTip(tipEl.dataset.tip, tipEl.dataset.ctip ?? "t_npc");
        addTip(e, tip, tipEl);
    } else {
        const itemTipEl = e.target.closest("[data-itemtip]");
        if (itemTipEl && itemTipEl instanceof HTMLElement) {
            const item = <StoredItem>JSON.parse(itemTipEl.dataset.itemtip);
            const tipContent = getItemTip(item);
            const tip = createBasicTipFromElement(tipContent, "t_item");
            tip.setAttribute("data-item-type", getItemRarity(item));

            const eqItem = isComparisonAllowed && getEqItemBySlot(eqItemSlots[item.cl]);
            if (!eqItem || (eqItem.id == item.id && eqItem.extendedID == item.extendedID)) {
                // Single item tip
                addTip(e, tip, itemTipEl);
            } else {
                // Double item tip (for comparison)
                const equippedTip = createBasicTipFromElement(getItemTip(eqItem), "t_item");
                appendComparisonNote(equippedTip);
                equippedTip.setAttribute("data-item-type", getItemRarity(eqItem));
                const doubleTip = document.createElement("div");
                doubleTip.classList.add("tip-multiple-wrapper");
                doubleTip.appendChild(equippedTip);
                doubleTip.appendChild(tip);
                addTip(e, doubleTip, itemTipEl, tip);
            }
        }
    }
}

function tipMove(e: MouseEvent | TouchEvent) {
    if (currentTip.el) {
        if (currentTip.owner.closest("body")) {
            positionTip(currentTip.el, e, currentTip.posEl);
        } else {
            clearTip();
        }
    }
}

function tipEnd(e: MouseEvent | TouchEvent) {
    if (!(e.target instanceof HTMLElement) || !currentTip.el) {
        return;
    }

    if ((e.target.closest("[data-tip]") ?? e.target.closest("[data-itemtip]")) == currentTip.owner && currentTip.owner != null) {
        clearTip();
    }
}

export function initTip() {
    document.addEventListener("mouseover", tipStart);
    document.addEventListener("touchstart", tipStart);

    document.addEventListener("mousemove", tipMove);
    // document.addEventListener("touchmove", tipMove);

    document.addEventListener("mouseout", tipEnd);
    document.addEventListener("touchend", tipEnd);
}
